/**
 * Auth Service
 * Phone OTP-based authentication
 */

import type { UserType } from "@shared/types";
import { toE164 } from "@/features/auth/utils/phone";
import { apiClient } from "@/lib/api-client";
import {
	clearTokens,
	getOrCreateDeviceId,
	markSignedIn,
	setDeviceId,
} from "@/lib/token-manager";

const API_BASE = "/api/v1/auth";

// ============================================================================
// TYPES
// ============================================================================

export interface SendOtpRequest {
	phone: string;
}

export interface SendOtpResponse {
	success: boolean;
	data: {
		message: string;
		expiresIn: number;
	};
	message: string;
}

export interface VerifyOtpRequest {
	phone: string;
	otp: string;
}

export interface FinishdUser {
	id: string;
	// nullable: Google-OAuth accounts sign up without a phone (collected later).
	phone: string | null;
	userType: UserType | null;
	/** Null until the user picks a language. */
	languagePreference: "en" | "hi" | null;
	isActive: boolean;
	createdAt: string;
	isNewUser: boolean;
	profileCompleted: boolean;
	profilePictureUrl: string | null;
}

export interface VerifyOtpResponse {
	success: boolean;
	data: {
		user: FinishdUser;
		// The backend echoes the id it labelled the session with. Access and
		// refresh tokens are HttpOnly cookies, so they are deliberately absent.
		deviceId: string;
		isNewUser: boolean;
	};
	message: string;
}

export interface GoogleAuthenticatedData {
	user: FinishdUser;
	deviceId: string;
	isNewUser: boolean;
}

export interface GoogleLinkRequiredData {
	linkRequired: true;
	email: string;
}

export interface GoogleSigninResponse {
	success: boolean;
	data: GoogleAuthenticatedData | GoogleLinkRequiredData;
	message: string;
}

export interface RefreshTokenRequest {
	refreshToken: string;
}

export interface RefreshTokenResponse {
	success: boolean;
	data: {
		accessToken: string;
	};
	message: string;
}

export interface SetUserTypeRequest {
	userType: UserType;
}

export interface SetUserTypeResponse {
	success: boolean;
	data: {
		user: FinishdUser;
	};
	message: string;
}

export interface UpdateLanguagePreferenceResponse {
	success: boolean;
	data: {
		user: FinishdUser;
	};
	message: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Send OTP to phone number
 * @param phone - E.164 number (e.g. "+14155550123"); a bare India national
 *   number is still accepted and sent as +91
 */
export async function sendOtp(phone: string): Promise<SendOtpResponse> {
	// The OTP screens show these failures inline, so skip the global toast.
	const response = await apiClient.post<SendOtpResponse>(
		`${API_BASE}/send-otp`,
		{
			phone: toE164(phone),
		},
		{ inlineErrors: true },
	);

	return response.data;
}

/**
 * Verify OTP and authenticate
 * @param phone - E.164 number, the same one the OTP was sent to
 * @param otp - 6-digit OTP (use "123456" in dev)
 */
export async function verifyOtp(
	phone: string,
	otp: string,
): Promise<VerifyOtpResponse> {
	const response = await apiClient.post<VerifyOtpResponse>(
		`${API_BASE}/verify-otp`,
		{
			phone: toE164(phone),
			otp,
			// Stable per-install id so the backend can label this device session.
			deviceId: getOrCreateDeviceId(),
		},
		{ inlineErrors: true },
	);

	// Tokens live in HttpOnly cookies set by the backend; just record that this
	// device now has a session so the client knows it can refresh.
	if (response.data.success) {
		markSignedIn();
		// Keep the id the backend actually stored, not the one we generated, so
		// the next login from this browser is recognised as the same device.
		if (response.data.data?.deviceId) {
			setDeviceId(response.data.data.deviceId);
		}
	}

	return response.data;
}

/**
 * Sign in with a Google ID token. The backend sets HttpOnly cookies on success;
 * returns either an authenticated session or a link-required signal when the
 * email already belongs to an existing phone account.
 */
export async function signInWithGoogle(
	idToken: string,
): Promise<GoogleSigninResponse> {
	const response = await apiClient.post<GoogleSigninResponse>(
		`${API_BASE}/google/signin`,
		{ idToken, deviceId: getOrCreateDeviceId() },
	);
	if (response.data.success && "user" in response.data.data) {
		markSignedIn();
	}
	return response.data;
}

/**
 * Link a Google identity to the signed-in account. It does not change
 * `users.email`; the Google account's own address is kept with the link.
 */
export async function linkGoogle(idToken: string): Promise<void> {
	await apiClient.post(`${API_BASE}/google/link`, { idToken });
}

/**
 * Lazy phone collection — step 1: send an OTP to a phone the authenticated
 * account wants to attach.
 */
export async function collectPhone(
	phone: string,
): Promise<{ success: boolean; data: { message: string }; message: string }> {
	const response = await apiClient.post(`${API_BASE}/collect-phone`, {
		phone: toE164(phone),
	});
	return response.data;
}

/**
 * Lazy phone collection — step 2: verify the OTP and attach the phone.
 */
export async function verifyCollectedPhone(
	phone: string,
	otp: string,
): Promise<{
	success: boolean;
	data: { phone: string | null };
	message: string;
}> {
	const response = await apiClient.post(`${API_BASE}/collect-phone/verify`, {
		phone: toE164(phone),
		otp,
	});
	return response.data;
}

/**
 * Refresh access token
 * @param refreshToken - Current refresh token
 */
export async function refreshToken(
	refreshToken: string,
): Promise<RefreshTokenResponse> {
	const response = await apiClient.post<RefreshTokenResponse>(
		`${API_BASE}/refresh-token`,
		{
			refreshToken,
		},
	);

	return response.data;
}

/**
 * Logout user. The backend revokes the current session via the session
 * cookie; no body. The local tokens are cleared even when that call fails,
 * and the failure is then rethrown, so the caller can tell the user the
 * server did not confirm the logout.
 */
export async function logout(): Promise<void> {
	try {
		await apiClient.post(`${API_BASE}/logout`);
	} finally {
		clearTokens();
	}
}

/**
 * Set user type (onboarding step)
 * @param userType - homeowner, designer, contractor, or realtor
 */
export async function setUserType(
	userType: "homeowner" | "designer" | "contractor" | "realtor",
): Promise<SetUserTypeResponse> {
	const response = await apiClient.patch<SetUserTypeResponse>(
		"/api/v1/users/me",
		{
			userType,
		},
	);

	return response.data;
}

/**
 * Update language preference
 * @param languagePreference - en or hi
 */
export async function updateLanguagePreference(
	languagePreference: "en" | "hi",
): Promise<UpdateLanguagePreferenceResponse> {
	const response = await apiClient.patch<UpdateLanguagePreferenceResponse>(
		"/api/v1/users/me",
		{
			languagePreference,
		},
	);

	return response.data;
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<{
	success: boolean;
	data: { user: FinishdUser };
}> {
	const response = await apiClient.get("/api/v1/users/me");
	return response.data;
}

/**
 * Refresh current user from backend and return a FinishdUser
 * suitable for updating the auth store. Returns null on failure.
 */
export async function refreshCurrentUser(): Promise<FinishdUser | null> {
	try {
		const response = await apiClient.get("/api/v1/users/me");
		return toFinishdUser(response.data?.data?.user);
	} catch {
		return null;
	}
}

/** The user object the account endpoints return in `data.user`. */
interface AccountUserResponse {
	id?: string;
	phone: string | null;
	userType?: UserType | null;
	languagePreference?: "en" | "hi" | null;
	createdAt: string;
	profileCompleted?: boolean;
	profilePictureUrl?: string | null;
}

/**
 * Build the stored user from an account endpoint's `data.user`. The response
 * has no `isActive` or `isNewUser`, so those carry over from the stored user.
 */
function toFinishdUser(
	userData: AccountUserResponse | undefined,
): FinishdUser | null {
	if (!userData?.id) return null;

	const currentUser = JSON.parse(
		localStorage.getItem("finishd-auth-store") || "{}",
	)?.state?.user as FinishdUser | undefined;

	return {
		id: userData.id,
		phone: userData.phone,
		userType: userData.userType ?? null,
		languagePreference: userData.languagePreference ?? null,
		isActive: currentUser?.isActive ?? true,
		createdAt: userData.createdAt,
		isNewUser: currentUser?.isNewUser ?? false,
		profileCompleted: userData.profileCompleted ?? false,
		profilePictureUrl: userData.profilePictureUrl ?? null,
	};
}

function requireUser(userData: AccountUserResponse | undefined): FinishdUser {
	const user = toFinishdUser(userData);
	if (!user) throw new Error("The server did not return the user");
	return user;
}

/**
 * Make `role` the active role. The server refuses a role the user does not
 * hold (403 `ROLE_NOT_HELD`).
 */
export async function switchActiveRole(role: UserType): Promise<FinishdUser> {
	const response = await apiClient.put(
		"/api/v1/users/me/active-role",
		{ role },
		{ skipActiveRoleCheck: true, inlineErrors: true },
	);
	return requireUser(response.data?.data?.user);
}

/**
 * Add `role` to the account and make it active. The returned user's
 * `profileCompleted` describes the added role's profile.
 */
export async function addRole(role: UserType): Promise<FinishdUser> {
	const response = await apiClient.post(
		"/api/v1/users/me/roles",
		{ role },
		{ skipActiveRoleCheck: true, inlineErrors: true },
	);
	return requireUser(response.data?.data?.user);
}
